import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { COMPLIANT_LABEL, NON_COMPLIANT_LABEL } from "src/constants/constants";
import { Button, Column, Icon, Row, Sheet, Text, Loader } from "@amzn/meridian";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import { ObjectPresenceCheck, Evidence, isOverridden, resolveDisplayedComment } from "src/services/inspectionService";
import "./ComplianceDetailSheet.scss";
import ThemedTag from "./ThemedTag";
import OverrideIcon from "./OverrideIcon";
import EvidenceTabs from "./EvidenceTabs";

interface ObjectPresenceDetailSheetProps {
    open: boolean;
    onClose: () => void;
    check: ObjectPresenceCheck | null;
    onOverride?: (check: ObjectPresenceCheck) => void;
    isAnalysisPending?: boolean;
    evidence: Evidence[];
    presignedUrlMap: Record<string, string>;
    /** Human-readable question text from the workflow question config. */
    questionText?: string;
    /**
     * Answer-time comment from the parent InspectionResult. Object presence checks have no
     * comment of their own, so this is the question-level comment shared by every object.
     */
    userComment?: string | null;
    /**
     * Override justification from the parent InspectionResult. Also question-level: overriding
     * any single object writes it, so all objects in the question show the same text.
     */
    overrideReason?: string | null;
}

const ObjectPresenceDetailSheet: React.FC<ObjectPresenceDetailSheetProps> = ({
    open,
    onClose,
    check,
    onOverride,
    isAnalysisPending = false,
    evidence,
    presignedUrlMap,
    questionText,
    userComment,
    overrideReason,
}) => {
    const [expandedUrl, setExpandedUrl] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<"overridden" | "original">("overridden");

    useEffect(() => {
        setExpandedUrl(null);
        setActiveTab("overridden");
    }, [check, open]);

    if (!check) {
        return null;
    }

    const evidenceUrls = evidence
        .map((e) => presignedUrlMap[e.uri])
        .filter(Boolean);

    const shouldShowTabs = isOverridden(check)
        && evidence.length > 0;

    const isOriginalTabActive = shouldShowTabs && activeTab === "original";
    // originalIsCompliant is the compliance state before the override was applied (the AI's original determination).
    // It is only present when the result has been overridden. When the result is not overridden,
    // is_compliant itself represents the original (and current) value.
    const displayCompliant = isOriginalTabActive && check.originalIsCompliant !== undefined ? check.originalIsCompliant : check.is_compliant;

    const commentText = resolveDisplayedComment(
        isOverridden(check), isOriginalTabActive, overrideReason, userComment
    );

    return (
        <>
        <Sheet type="overlay" side="bottom" open={open} onClose={onClose} className="compliance-detail-sheet">
            <Column spacing="400" spacingInset="500">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="h300">Compliance Details</Text>
                    <Button onClick={onClose} type="icon">
                        <Icon tokens={closeSmallTokens}>Close compliance details</Icon>
                    </Button>
                </Row>

                {questionText && (
                    <Text type="b200" color="secondary">
                        {questionText}
                    </Text>
                )}

                <Row alignmentHorizontal="justify" alignmentVertical="top">
                    <Column spacing="100">
                        <Text type="b100" color="secondary">Object</Text>
                        <Text type="b200" color="primary">{check.object_name}</Text>
                    </Column>
                    <Row spacing="200" alignmentVertical="center">
                        <ThemedTag
                            text={isAnalysisPending ? "Analyzing..." : displayCompliant ? COMPLIANT_LABEL : NON_COMPLIANT_LABEL}
                            type={isAnalysisPending ? "neutral" : displayCompliant ? "success" : "error"}
                        />
                        {/* Object presence questions are always AI-analyzed, matching the
                            hardcoded source tag on ObjectPresenceCheckpointCard. */}
                        {!isAnalysisPending && <ThemedTag text="AI" type="neutral" />}
                    </Row>
                </Row>

                {isAnalysisPending ? (
                    <Loader type="circular" size="large" />
                ) : (
                    <>
                        <Column spacing="100">
                            <Text type="b100" color="secondary">Answer</Text>
                            <Row spacing="200" alignmentVertical="center">
                                <Column spacing="200">
                                <Text type="b200" color="primary">{displayCompliant ? "YES" : "NO"}</Text>
                                {isOverridden(check) && activeTab !== "original" && (
                                    <ThemedTag text="Manually Overridden" type="override" icon={<OverrideIcon />} />
                                )}
                                </Column>
                                
                            </Row>
                        </Column>

                        {(!shouldShowTabs || activeTab === "original") && check.ai_reason && (
                            <Column spacing="100">
                                <Text type="b100" color="secondary">Reason</Text>
                                <Text type="b200" color="primary">{check.ai_reason}</Text>
                            </Column>
                        )}

                        {shouldShowTabs ? (
                            <EvidenceTabs
                                activeTab={activeTab}
                                onTabChange={setActiveTab}
                                overriddenEvidences={check.overriddenEvidences ?? []}
                                originalEvidences={evidence}
                                presignedUrlMap={presignedUrlMap}
                                onImageExpand={setExpandedUrl}
                            />
                        ) : evidenceUrls.length > 0 && (
                            <Row spacing="300" wrap="down">
                                {evidenceUrls.map((url, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        className="compliance-detail-sheet__image-button"
                                        onClick={() => setExpandedUrl(url)}
                                        aria-label={`Expand evidence ${idx + 1}`}
                                    >
                                        <img
                                            src={url}
                                            alt={`Evidence ${idx + 1}`}
                                            className={`compliance-detail-sheet__image${evidenceUrls.length === 1 ? " compliance-detail-sheet__image--large" : ""}`}
                                        />
                                    </button>
                                ))}
                            </Row>
                        )}

                        {commentText && (
                            <Column spacing="100">
                                <Text type="h200" color="primary">Comments</Text>
                                <Text type="b200" color="secondary">{commentText}</Text>
                            </Column>
                        )}

                        {!isAnalysisPending && onOverride && (
                            <button
                                className="compliance-detail-sheet__override-btn"
                                onClick={() => onOverride(check)}
                            >
                                MANUAL OVERRIDE
                            </button>
                        )}
                    </>
                )}
            </Column>
        </Sheet>

        {expandedUrl && ReactDOM.createPortal(
            <div
                className="compliance-detail-sheet__lightbox"
                aria-label="Expanded evidence image"
                onClick={() => setExpandedUrl(null)}
            >
                <button
                    type="button"
                    className="compliance-detail-sheet__lightbox-close"
                    onClick={() => setExpandedUrl(null)}
                    aria-label="Close expanded image"
                >
                    &times;
                </button>
                <img
                    src={expandedUrl}
                    alt="Expanded evidence"
                    className="compliance-detail-sheet__lightbox-image"
                    onClick={(e) => e.stopPropagation()}
                />
            </div>,
            document.body
        )}
        </>
    );
};

export default ObjectPresenceDetailSheet;
